import { withAuthenticationRequired } from '@auth0/auth0-react';
import { useToast } from '@chakra-ui/react';
import { ApmRoute } from '@elastic/apm-rum-react';
import {
  LayoutHeader,
  LayoutMain,
  LayoutWrapper,
  SkipToMainContent,
} from 'features/common/components/Layouts';
import { LoadingScreen } from 'features/common/components/LoadingScreen';
import { AdminOwnerRoute } from 'features/common/components/OrganizationAppAccess';
import ScriptInjector from 'features/common/components/ScriptInjector';
import { env } from 'features/common/config/envConfig';
import { useSetDocumentTitle } from 'features/common/hooks/useSetDocumentTitle';
import { restrictedSitecoreEmails } from 'features/common/validations/matchers';
import {
  getGainsightScript,
  useGainsight,
} from 'features/domains/gainsight/gainsight';
import { SSOProvider } from 'features/sso';
import Subscriptions from 'features/subscriptions';
import SubscriptionDetails from 'features/subscriptions/SubscriptionDetails';
import {
  subscriptionRoute,
  subscriptionsRoute,
} from 'features/subscriptions/routes';
import UnifiedTrackingContainer from 'features/unified-tracking/components/UnifiedTrackingContainer';
import { useGetCurrentUserQuery } from 'gql/graphql';
import { ApolloProvider } from 'lib/@apollo/client/ApolloProvider';
import { AzureFeaturesProvider } from 'lib/featureFlags/azure-feature-flags';
import { PersonalizeTracking } from 'lib/personalize/personalizeTracking';
import { useEffect } from 'react';
import { Redirect, Switch, useLocation } from 'react-router-dom';
import AdminContainer from '../../admin/components/AdminContainer';
import { errorCallback } from '../../common/apiUtils/queryHelper';
import { history } from '../../common/history/history';
import { useOrgEnvironmentDeps } from '../../common/utils/orgEnvironments';
import HomeContainer from '../../home/components/HomeContainer';
import ProfileContainer from '../../profile/components/ProfileContainer';
import { TopBar } from '../../topbar/components/TopBar';
import {
  ErrorPage,
  InvalidInvitationPage,
  NoAccessPage,
  NoOrgAccessPage,
  PageNotFound,
} from './CustomErrorPages';
import { DrawerManager } from './DrawerManager/DrawerManager';
import { InvitationAuth } from './InvitationAuth';
import { DeveloperRoutes } from 'features/developer/routes/DeveloperRoutes';
import { MARKETPLACE_DEVELOPER_STUDIO_ROUTES } from '../../developer/config/routes';
import { MarketplaceRoutes } from 'features/marketplace/routes/MarketplaceRoutes';
import { MARKETPLACE_HOMEPAGE_ROUTES } from '../../marketplace/config/routes';
import { MyAppsRoutes } from 'features/my-apps/routes/MyAppsRoutes';
import { MY_APPS_ROUTES } from '../../my-apps/config/routes';

export const MainContainerComp = () => {
  useSetDocumentTitle();
  const { organization, isLoading: isOrgLoading } = useOrgEnvironmentDeps();
  const { data: { user } = {} } = useGetCurrentUserQuery();

  const orgIsNotInternal = organization && organization.type !== 'INTERNAL';
  const userIsNotInternal = user && !restrictedSitecoreEmails.test(user.email);
  const shouldInitializeGainsight =
    env?.toLowerCase() === 'production'
      ? orgIsNotInternal && userIsNotInternal
      : true;

  /* TEMPORARY FOR TRIGGERING ERROR TOASTS */
  const { search } = useLocation();
  const toast = useToast();

  const gainsightScript = getGainsightScript();
  useGainsight();

  useEffect(() => {
    const params = new URLSearchParams(search);
    const fakeCode = params.get('fakeCode');
    const fakeScope = params.get('fakeScope');
    if (fakeCode && fakeScope) {
      const err = { response: { status: parseInt(fakeCode) } };
      setTimeout(() => {
        errorCallback(err, fakeScope, toast);
      }, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AzureFeaturesProvider data={organization} isOrgDataLoading={isOrgLoading}>
      <SSOProvider>
        {(env?.toLowerCase() === 'staging' ||
          env?.toLowerCase() === 'production') &&
          window.location.hostname !== 'localhost' && <PersonalizeTracking />}
        {shouldInitializeGainsight &&
          window.location.hostname !== 'localhost' && (
            <ScriptInjector code={gainsightScript} id='gainsight' />
          )}
        <LayoutWrapper h='100vh' w='100vw' position='relative'>
          <SkipToMainContent />
          <LayoutHeader overflow={'visible'}>
            <TopBar />
          </LayoutHeader>
          <LayoutMain bg='chakra-subtle-bg' p={0}>
            <Switch>
              <ApmRoute
                exact
                path={subscriptionsRoute}
                component={Subscriptions}
              />
              <ApmRoute
                path={subscriptionRoute}
                component={SubscriptionDetails}
              />
              <Redirect
                from='/admin/members/invites'
                to={`/admin/invites/page/1${history.location.search}`}
              />
              <AdminOwnerRoute path='/admin' component={AdminContainer} />
              <Redirect
                from='/profile/manage'
                to={`/profile${history.location.search}`}
              />
              <ApmRoute path='/profile' component={ProfileContainer} />
              <ApmRoute exact path='/no-access' component={NoAccessPage} />
              <ApmRoute
                path={MARKETPLACE_DEVELOPER_STUDIO_ROUTES.ROOT}
                component={DeveloperRoutes}
              />
              <ApmRoute
                path={MARKETPLACE_HOMEPAGE_ROUTES.ROOT}
                component={MarketplaceRoutes}
              />
              <ApmRoute path={MY_APPS_ROUTES.ROOT} component={MyAppsRoutes} />
              <ApmRoute
                path='/unified-tracking'
                component={UnifiedTrackingContainer}
              />
              <ApmRoute exact path='/' component={HomeContainer} />
              <ApmRoute component={PageNotFound} />
            </Switch>
          </LayoutMain>
          <ApmRoute component={DrawerManager} />
        </LayoutWrapper>
      </SSOProvider>
    </AzureFeaturesProvider>
  );
};

const AuthMainContainer = withAuthenticationRequired(MainContainerComp, {
  onRedirecting: () => (
    <LoadingScreen data-testid='loading-component' message='Loading…' />
  ),
});

export const MainContainer = () => {
  return (
    <ApolloProvider>
      <Switch>
        <ApmRoute
          exact
          path='/no-organization-access'
          component={NoOrgAccessPage}
        />
        <ApmRoute
          exact
          path='/no-valid-invitation'
          component={InvalidInvitationPage}
        />
        <ApmRoute exact path='/something-went-wrong' component={ErrorPage} />
        <ApmRoute path='/account/login' component={InvitationAuth} />
        <ApmRoute path='*' component={AuthMainContainer} />
      </Switch>
    </ApolloProvider>
  );
};
